/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { unset } from "lodash";
import { handleProcessClaim, validateField } from "../customSubmitClaim";
import { transformTransactionKeys } from "../utils";
import { toggleFieldError } from "commonAssets/js/helpers";
import { i18n } from "commonAssets/js/constants";

/**
 * Validates a form.
 *
 * @param {Object} options - The options object.
 * @param {Event|string} options.event - The event object.
 * @param {CustomSubmitClaim} options.ctx - The current context.
 * @returns {void}
 */
export function validateForm({ event, ctx }) {
    if (event === 'init_claim') {
        organizeContactSection(ctx);
        organizeSignatureSection(ctx);

        // Move general instructions into claim deadline fieldset
        const claimDeadlineFieldset = document.querySelector('#claimFormDeadline > fieldset');
        const generalInstructions = document.querySelector('#generalInstructionsWrapper');
        claimDeadlineFieldset.appendChild(generalInstructions);

        // Insert part D paragraph before certification paragraph
        const certificationSection = document.querySelector('div#certification');
        const partDParagraph = document.querySelector('div[data-field-id="_part_d_paragraph"]');
        certificationSection.parentNode.insertBefore(partDParagraph, certificationSection);

        // Hide the now-empty claim fields container: holdings moved into the
        // transactions tables (Part B), and the remaining claimant fields were
        // relocated into Part A / signature / certification above.
        const claimFields = document.querySelector('#claim-fields');
        if (claimFields) claimFields.classList.add('d-none');

        // Attach numeric formatting / date validation to the initial transaction rows
        ctx.formElement.querySelectorAll('.transactions input[type="number"]').forEach(input => {
            input.addEventListener('blur', formatNumericValue);
        });
        ctx.formElement.querySelectorAll('.transactions input[type="date"]').forEach(input => {
            input.addEventListener('change', validateField);
        });

        // Restrict the SSN/TIN field to the last 4 digits only
        setupLastFourSsn(ctx);

        // Disable email validation for paper claim submissions
        const isPaper = window.location.search.includes('paper_claim=true')
        if (isPaper) disableEmailValidation()
    }

    // On submission, enforce the transaction rules for Part B:
    // - at least one purchase is required
    // - any started purchase/sale row must have a date, share count, and price
    if (event === 'process_claim') {
        validatePartBTransactions(ctx);
    }
}

/**
 * Restricts the SSN/TIN input to the last four digits (numeric only).
 *
 * @param {CustomSubmitClaim} ctx - The current context.
 */
function setupLastFourSsn(ctx) {
    const ssn = ctx.getFormInput('ssn_tin');
    if (!ssn) return;

    ssn.setAttribute('maxlength', '4');
    ssn.setAttribute('inputmode', 'numeric');
    ssn.addEventListener('input', () => {
        ssn.value = ssn.value.replace(/\D/g, '').slice(0, 4);
    });
}

/**
 * Validates the Part B schedule of transactions on submission.
 *
 * Purchases (part_b_2) require at least one transaction. For both purchases
 * and sales (part_b_3), any row that has been started (any field filled in)
 * must include a trade date, number of shares, and price per share.
 *
 * Sets `ctx.claim.flags.valid = false` and flags the offending fields when a
 * rule is violated so the submission is blocked.
 *
 * @param {CustomSubmitClaim} ctx - The current context.
 */
function validatePartBTransactions(ctx) {
    const REQUIRED_SUFFIXES = ['trade_date', 'number_of_shares', 'price_per_share'];

    const validateTable = (tableId, { requireAtLeastOne, atLeastOneMessage } = {}) => {
        const rows = getTransactionRows(tableId);
        let anyStarted = false;

        rows.forEach(inputs => {
            const started = inputs.some(input => input.value.trim() !== '');
            if (!started) return;
            anyStarted = true;

            inputs.forEach(input => {
                const isRequiredField = REQUIRED_SUFFIXES.some(suffix => input.name.includes(suffix));
                if (isRequiredField && input.value.trim() === '') {
                    toggleFieldError(input, true, i18n.getTranslation('required'));
                    ctx.claim.flags.valid = false;
                }
            });
        });

        if (requireAtLeastOne && !anyStarted) {
            ctx.claim.flags.valid = false;
            const firstDate = document.querySelector(`#transactions [data-table-id="${tableId}"][name*="trade_date"]`);
            if (firstDate) toggleFieldError(firstDate, true, atLeastOneMessage);
        }
    };

    // Purchases: at least one required
    validateTable('part_b_2', {
        requireAtLeastOne: true,
        atLeastOneMessage: 'At least one purchase is required'
    });

    // Sales: optional, but completed rows must be fully filled
    validateTable('part_b_3');
}

/**
 * Groups the inputs of a transaction table by row.
 *
 * @param {string} tableId - The transaction table id (e.g. "part_b_2").
 * @returns {HTMLElement[][]} An array of rows, each an array of its inputs.
 */
function getTransactionRows(tableId) {
    const inputs = document.querySelectorAll(`#transactions [data-table-id="${tableId}"]`);
    const rows = new Map();

    inputs.forEach(input => {
        const row = input.closest('tr.transaction-row');
        if (!row) return;
        if (!rows.has(row)) rows.set(row, []);
        rows.get(row).push(input);
    });

    return Array.from(rows.values());
}

/**
 * Processes a claim for a custom member.
 *
 * Transactions are keyed by numeric item-id (B.1 = 1101 open holdings,
 * B.2 = 1201+ purchases, B.3 = 1301+ sales, B.4 = 1401 close holdings).
 * Keys are prefixed with their section.table ("B.1:", "B.2:", ...). The two
 * holdings rows are merged into `fields`; purchases/sales remain in
 * `transactions`. `securities_transactions` is left empty so the backend
 * securities mapping builds the internal rows from the prefixed keys.
 *
 * @param {CustomSubmitClaim} ctx - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(ctx) {
    const claim = handleProcessClaim.call(ctx, validateForm);

    // Keep only numeric (transaction) item-id keys, then prefix with section.table
    const numericTransactions = Object.fromEntries(
        Object.entries(claim.data.transactions).filter(([key]) => !isNaN(key))
    );
    const prefixedTransactions = transformTransactionKeys(numericTransactions);

    // Pull holdings (B.1 / B.4) out of transactions and into fields
    const {
        ['1101']: openingHoldings,
        ['1401']: closingHoldings,
        ...restTransactions
    } = prefixedTransactions;

    return {
        ...claim,
        data: {
            ...claim.data,
            contact: {
                ...claim.data.contact,
                'ag_PAPER': window.location.search.includes('paper_claim=true')
            },
            fields: {
                ...claim.data.fields,
                ...openingHoldings,
                ...closingHoldings,
                'Claim Form Type': 'Individual',
                'Subject to Backup Withholding': ctx.getFormInput('subject_to_backup_withholding').checked ? 'yes' : 'no'
            },
            transactions: restTransactions,
            securities_transactions: []
        }
    };
}

/**
 * Handles the row added event.
 *
 * @param {HTMLElement} row - The row element.
 */
export function onTransactionRowAdded(row) {
    row.querySelectorAll('input[type="number"]').forEach(input => {
        input.addEventListener('blur', formatNumericValue);
    });
    row.querySelectorAll('input[type="date"]').forEach(input => {
        input.addEventListener('change', validateField);
    });
}

/**
 * Handles the row removed event.
 *
 * @param {HTMLElement} row - The row element.
 */
export function onTransactionRowRemoved(row) {
    row.querySelectorAll('input[type="number"]').forEach(input => {
        input.removeEventListener('blur', formatNumericValue);
    });
    row.querySelectorAll('input[type="date"]').forEach(input => {
        input.removeEventListener('change', validateField);
    });
}

/**
 * Formats an amount input to its step's decimal places on blur.
 *
 * @param {Event} event - The event object.
 * @returns {void}
 */
function formatNumericValue(event) {
    const input = event.target;
    const value = input.value;

    // If the value is empty or not a valid number, clear it or leave it
    if (value === '' || isNaN(parseFloat(value))) {
        input.value = '';
        return;
    }

    // Format to the number of decimal places defined by the input's step
    const numericValue = parseFloat(value);
    const step = input.getAttribute('step') || '';
    const decimalPlaces = step.includes('.') ? step.split('.')[1].length : 0;
    input.value = numericValue.toFixed(decimalPlaces);
}

/**
 * Organizes the contact section of the form.
 *
 * @param {CustomSubmitClaim} ctx - The current context.
 */
function organizeContactSection(ctx) {
    const fieldset = findField('last_name').parentElement;

    // Define field groups and their reference points
    const fieldGroups = [
        {
            fields: [
                'custodian_name',
                'joint_beneficial_owner_name',
                'beneficial_owner_name'
            ],
            referenceField: 'last_name'
        },
        {
            fields: [
                'ssn_tin',
                'account_number'
            ],
            referenceField: 'confirm_email_address'
        }
    ];

    // Process each field group
    fieldGroups.forEach(({ fields, referenceField }) => {
        const referenceNode = findField(referenceField);
        moveFields(fields, referenceNode, fieldset);
    });

    // Hide the first/last name fields. They remain in the DOM (so they still
    // anchor the field moves above and post to their class_member columns) but
    // are not shown to the claimant.
    ['first_name', 'last_name'].forEach(fieldName => {
        findField(fieldName).classList.add('d-none');
    });
}

function organizeSignatureSection(ctx) {
    const container = document.querySelector('div.warranties');
    const lastParagraph = container.querySelector('p:last-child');
    const subjectToBackupWithholding = findField('subject_to_backup_withholding');
    container.insertBefore(subjectToBackupWithholding, lastParagraph);
}

/**
 * Moves a group of fields to a new location in the form.
 *
 * @param {string[]} fieldNames - Array of field names to move
 * @param {Node} referenceNode - The node to insert before
 * @param {HTMLElement} container - The container element
 */
function moveFields(fieldNames, referenceNode, container) {
    fieldNames.forEach(fieldName => {
        const field = findField(fieldName);
        container.insertBefore(field, referenceNode.nextSibling);
    });
}

/**
 * Finds a field by name.
 *
 * @param {string} fieldName - The name of the field to find
 * @returns {HTMLElement} The field element
 */
function findField(fieldName) {
    const container = document.querySelector(`div[data-field-id="_${fieldName}"]`);
    if (!container) {
        const input = document.querySelector(`input[name="${fieldName}"], textarea[name="${fieldName}"], select[name="${fieldName}"]`);
        if (!input) {
            throw new Error(`Field "${fieldName}" not found`);
        }
        return input.type === 'checkbox' ? input.parentElement.parentElement : input.parentElement;
    }
    return container;
}

/**
 * Removes event listeners when the form is unloaded.
 *
 * @param {CustomSubmitClaim} ctx - The current context.
 */
export function onFormUnloaded(ctx) {
    console.log('[Form Unloaded]');
}

/**
  * Disables email validation logic for email fields
  */
function disableEmailValidation() {
  const emailElements = document.querySelectorAll('[data-validate*="email"]');
  if(emailElements.length === 0) return;
  emailElements.forEach(el => {
    unset(el.dataset, 'validate');
    el.required = undefined;
  });
}
